import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import { useOnMount } from '../../../../components/common/Hooks';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setDirection, setPage, setSortField } from '../../../../redux/slices/PaginationSlice';
import { completeOrder, deleteOrder, getOrdersPage, upsertOrder } from './OrdersService';
import { isMobile } from 'react-device-detect';
import { ToggleFilterButton } from '../../../../components/buttons/ToggleFilterButton';
import { OrderForm } from './OrderForm';
import { Spinner } from 'react-bootstrap';
import TableComponent from '../../../../components/table/TableComponent';
import { ConfirmationModal } from '../../../../components/modal/ConfirmationModal';
import { OrderFilter } from './OrderFilter';
import { CompleteOrderForm } from './complete/CompleteOrderForm';
import { DeleteButton } from '../../../../components/buttons/DeleteButton';
import { CompleteButton } from '../../../../components/buttons/CompleteButton';
import { EditButton } from '../../../../components/buttons/EditButton';

export const OrdersPage = () => {

    const { t, i18n } = useTranslation();
    const alert = useAlert();
    const onMount = useOnMount();
    const [loading, setLoading] = useState(true);
    const dispatch = useDispatch();
    const pagination = useSelector((state) => state.pagination);
    const [filterValue, setFilterValue] = useState({
        status: null,
        from: null,
        till: null,
    });
    const [orders, setOrders] = useState([]);
    const statuses = ['CREATED', 'COMPLETED', 'DELETED'];
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [isOrderCompleteOpen, setIsOrderCompleteOpen] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [selectedCompleteOrderId, setSelectedCompleteOrderId] = useState(null);
    const handleCloseModal = () => setIsConfirmOpen(false);

    useEffect(() => {
        if (onMount) {
            init();
        } else {
            loadOrders();
        }
    }, [pagination.page, pagination.sortByField, pagination.sortingDirection]);

    const init = () => {
        if (pagination.sortByField === 'orderedAt'
            && pagination.page === 0
            && pagination.sortingDirection === 'DESC') {
            loadOrders();
        } else {
            dispatch(setSortField('orderedAt'));
            dispatch(setPage(0));
            dispatch(setDirection('DESC'));
        }
    };

    const loadOrders = () => {
        let request = {
            page: pagination.page,
            size: pagination.size,
            ascDesc: pagination.sortingDirection,
            sortField: pagination.sortByField,
            lang: i18n.language.toUpperCase(),
            title: '',
            status: filterValue.status,
            from: filterValue.from,
            till: filterValue.till,
        };
        setLoading(true);
        getOrdersPage(request)
            .then((response) => {
                let content = response.data.content.map(order => {
                    return { ...order, statusType: order.status, status: t('order:statuses.' + order.status) };
                });
                setOrders({ totalElements: response.data.totalElements, content: content });
            })
            .catch(() => alert.error(t('alert:loadingError')))
            .finally(() => setLoading(false));
    };

    const handleSubmit = (order) => {
        upsertOrder(order)
            .then(() => {
                alert.success(t('alert:saveSuccess'));
                loadOrders();
                setSelectedOrderId(null);
            })
            .catch(() => alert.error(t('alert:saveError')));
    };

    const handleComplete = (request) => {
        completeOrder(request)
            .then(() => {
                alert.success(t('alert:saveSuccess'));
                loadOrders();
                setSelectedCompleteOrderId(null);
                setIsOrderCompleteOpen(false);
            })
            .catch(() => alert.error(t('alert:saveError')));
    };

    const handleCancel = () => {
        setSelectedOrderId(null);
    };

    const openConfirmation = (order) => {
        setSelectedOrderId(order.id);
        setIsConfirmOpen(true);
    };

    const doDeleteOrder = () => {
        deleteOrder(selectedOrderId)
            .then(() => {
                alert.success(t('alert:deleteSuccess'));
                loadOrders();
            })
            .catch(() => alert.error(t('alert:deleteError')));
        handleCloseModal();
    };

    useEffect(() => {
        if (!isConfirmOpen && selectedOrderId != null) {
            document.getElementById('createButton').click();
        }
    }, [selectedOrderId]);

    const editOrder = (order) => {
        setSelectedOrderId(order.id);
    };

    const showCompleteOrder = (order) => {
        setSelectedCompleteOrderId(order.id);
        setIsOrderCompleteOpen(true);
    };

    const handleCompleteOrderCancel = () => {
        setSelectedCompleteOrderId(null);
        setIsOrderCompleteOpen(false);
    };

    const isDisabled = (order) => {
        return order.statusType === 'COMPLETED' || order.statusType === 'DELETED';
    };

    const getButtons = () => {
        return [
            {
                element: <CompleteButton onClick={showCompleteOrder}
                                         disabled={isDisabled} />,
            },
            {
                element: <EditButton onClick={editOrder}
                                     disabled={isDisabled} />,
            },
            {
                element: <DeleteButton onClick={openConfirmation}
                                       disabled={isDisabled} />,
            },
        ];
    };

    return (
        <>
            <div className={isMobile ? 'mobile-filter-container' : 'filter-container'}>
                <OrderForm onSubmit={handleSubmit}
                           onCancel={handleCancel}
                           orderId={selectedOrderId}
                           isOrdersPage={true}
                           shortage={null} />
                {isMobile ? <ToggleFilterButton></ToggleFilterButton> : <></>}
                <OrderFilter doFilter={loadOrders} filter={filterValue} statusTypes={statuses}></OrderFilter>
            </div>
            <CompleteOrderForm
                isShowing={isOrderCompleteOpen}
                onSubmit={handleComplete}
                onCancel={handleCompleteOrderCancel}
                orderId={selectedCompleteOrderId}>
            </CompleteOrderForm>
            <ConfirmationModal
                message={t('order:deleteOrderConfirm')}
                isShowing={isConfirmOpen}
                onConfirm={doDeleteOrder}
                onClose={handleCloseModal}>
            </ConfirmationModal>
            {loading ? (
                <Spinner animation='border' className='container' />
            ) : (<TableComponent
                    model={{
                        partnerName: '',
                        orderedBy: '',
                        orderedAt: '',
                        status: '',
                    }}
                    translationsGroup='order:'
                    columnsNames={[
                        'partnerName',
                        'orderedBy',
                        'orderedAt',
                        'status',
                    ]}
                    totalElements={orders.totalElements}
                    contents={orders.content}
                    buttons={getButtons()}
                />
            )}
        </>
    );
};